import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  BarChart3,
  Users,
  Clock,
  TrendingUp,
  MapPin,
  Phone,
  Calendar,
  Award,
  CheckCircle,
  AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResponderAnalytics = ({ user }) => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/responder/analytics`);
      setAnalytics(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load analytics");
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="va-blue-bar h-2"></div>
        <div className="container mx-auto px-4 py-16 max-w-2xl text-center">
          <AlertCircle className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              My Impact
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Hero Stats */}
        <Card className="card-valor mb-8 bg-gradient-to-r from-primary to-primary/80 text-white">
          <CardContent className="p-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="text-center sm:text-left">
                <p className="text-white/80 mb-2">Veterans You've Supported</p>
                <p className="text-6xl font-bold">{analytics?.veterans_helped || 0}</p>
                <p className="text-white/60 text-sm mt-2">
                  Thank you for making a difference!
                </p>
              </div>
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
                <Award className="w-12 h-12 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Bar */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Badge 
            variant={analytics?.is_available ? "default" : "secondary"}
            className={analytics?.is_available ? "bg-green-600" : ""}
          >
            {analytics?.is_available ? "Currently Available" : "Not Available"}
          </Badge>
          {analytics?.training_completed && (
            <Badge className="bg-blue-600">
              <CheckCircle className="w-3 h-3 mr-1" />
              Training Complete
            </Badge>
          )}
          <span className="text-sm text-muted-foreground">
            Member since {formatDate(analytics?.member_since)}
          </span>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="card-valor">
            <CardContent className="p-4 text-center">
              <Users className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{analytics?.total_requests_accepted || 0}</p>
              <p className="text-xs text-muted-foreground">Requests Accepted</p>
            </CardContent>
          </Card>
          
          <Card className="card-valor">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{analytics?.completion_rate || 0}%</p>
              <p className="text-xs text-muted-foreground">Completion Rate</p>
            </CardContent>
          </Card>
          
          <Card className="card-valor">
            <CardContent className="p-4 text-center">
              <Clock className="w-8 h-8 text-secondary mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">
                {analytics?.avg_response_time_minutes 
                  ? `${Math.round(analytics.avg_response_time_minutes)} min` 
                  : "N/A"}
              </p>
              <p className="text-xs text-muted-foreground">Avg Response Time</p>
            </CardContent>
          </Card>
          
          <Card className="card-valor">
            <CardContent className="p-4 text-center">
              <MapPin className="w-8 h-8 text-primary mx-auto mb-2" />
              <p className="text-2xl font-bold text-foreground">{analytics?.request_types?.in_person || 0}</p>
              <p className="text-xs text-muted-foreground">In-Person Visits</p>
            </CardContent>
          </Card>
        </div>

        {/* Request Type Breakdown */}
        <Card className="card-valor mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
              <BarChart3 className="w-5 h-5 text-primary" />
              Support Types
            </CardTitle>
            <CardDescription>How you've helped veterans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-lg">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{analytics?.request_types?.in_person || 0}</p>
                  <p className="text-sm text-muted-foreground">In-Person Support</p>
                </div>
              </div>
              <div className="flex items-center gap-4 p-4 bg-secondary/5 rounded-lg">
                <div className="w-12 h-12 bg-secondary/20 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-secondary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{analytics?.request_types?.call || 0}</p>
                  <p className="text-sm text-muted-foreground">Phone/Video Calls</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Monthly Breakdown */}
        {analytics?.monthly_breakdown && Object.keys(analytics.monthly_breakdown).length > 0 && (
          <Card className="card-valor">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope' }}>
                <Calendar className="w-5 h-5 text-primary" />
                Monthly Activity
              </CardTitle>
              <CardDescription>Veterans supported each month</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {Object.entries(analytics.monthly_breakdown)
                  .sort((a, b) => b[0].localeCompare(a[0]))
                  .slice(0, 6)
                  .map(([month, count]) => {
                    const date = new Date(month + "-01");
                    const monthName = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                    const maxCount = Math.max(...Object.values(analytics.monthly_breakdown));
                    const percentage = (count / maxCount) * 100;
                    
                    return (
                      <div key={month} className="flex items-center gap-4">
                        <span className="w-20 text-sm text-muted-foreground">{monthName}</span>
                        <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary rounded-full transition-all duration-500"
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <span className="w-8 text-sm font-medium text-foreground">{count}</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {analytics?.veterans_helped === 0 && (
          <Card className="card-valor mt-8">
            <CardContent className="p-8 text-center">
              <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-foreground mb-2">Ready to Make an Impact?</h3>
              <p className="text-muted-foreground mb-4">
                You haven't supported any veterans yet. Make sure your availability is turned on in your dashboard!
              </p>
              <Button onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ResponderAnalytics;
